import { Head, Link, useForm, usePage } from "@inertiajs/react"
import { ArrowLeft, Mail, Plus } from "lucide-react"
import { CoachLayout } from "../components/CoachLayout"
import { CopyButton } from "@/components/CopyButton"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"

function buildEmailTemplate({ prospectName, coachName, link }) {
  const firstName = prospectName.trim().split(/\s+/)[0] || "friend"
  const assessmentBlurb =
    "I'd love for you to take a short Kingdom Factor assessment before our next call. You'll fill out the Integrated Life Audit and answer a few questions about your business, your goals, and what matters most to you — about 10–15 minutes. At the end you'll get a personalized report on how a Christian business peer group could help you in the areas you care about, and I'll use it to shape our follow-up."

  return `Hi ${firstName},

Thanks again for the conversation — I really enjoyed hearing your story.

${assessmentBlurb}

Here's your private link (it'll sign you in automatically):

${link}

Let me know if anything feels unclear. Talk soon.

— ${coachName}`
}


function GeneratedInviteCard({ invite, coachName }) {
  const emailBody = buildEmailTemplate({
    prospectName: invite.name,
    coachName: coachName || "Your coach",
    link: invite.link,
  })
  const mailtoHref = `mailto:${encodeURIComponent(invite.email)}?subject=${encodeURIComponent(
    "Your Kingdom Factor assessment",
  )}&body=${encodeURIComponent(emailBody)}`

  return (
    <Card className="border-kf-red/30 bg-kf-red/5 mb-6">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Mail className="w-5 h-5 text-kf-red" />
          Invite ready for {invite.name}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        <div>
          <Label className="text-xs uppercase tracking-wide text-zinc-500">
            Magic link — reusable for 30 days
          </Label>
          <div className="mt-1 flex gap-2">
            <Input
              value={invite.link}
              readOnly
              onFocus={(e) => e.target.select()}
              className="font-mono text-xs bg-white"
            />
            <CopyButton text={invite.link} label="Copy link" />
          </div>
          <p className="text-xs text-zinc-500 mt-1">
            Anyone with this link signs in as {invite.email} automatically and
            can come back to their assessment any time. Send it only to them.
          </p>
        </div>

        <div>
          <div className="flex items-center justify-between mb-1">
            <Label className="text-xs uppercase tracking-wide text-zinc-500">
              Suggested email — edit to your voice
            </Label>
            <CopyButton text={emailBody} label="Copy email" />
          </div>
          <Textarea
            value={emailBody}
            readOnly
            onFocus={(e) => e.target.select()}
            rows={14}
            className="font-mono text-xs bg-white"
          />
        </div>

        <div className="flex flex-wrap gap-2 pt-1">
          <a href={mailtoHref}>
            <Button type="button" variant="gradient">
              <Mail className="w-4 h-4 mr-1.5" /> Open in email client
            </Button>
          </a>
          <Link href="/app/coach/prospects">
            <Button type="button" variant="outline">
              <Plus className="w-4 h-4 mr-1.5" /> Invite another
            </Button>
          </Link>
        </div>
      </CardContent>
    </Card>
  )
}

export default function NewIntakeInvite({ coachDisplayName }) {
  const { flash } = usePage().props
  const generated = flash?.generated_invite
  const form = useForm({ name: "", email: "" })

  const submit = (e) => {
    e.preventDefault()
    form.post("/app/coach/intake_invites", {
      preserveScroll: true,
      onSuccess: () => form.reset(),
    })
  }

  return (
    <CoachLayout>
      <Head title="Invite a prospect" />

      <div className="max-w-2xl mx-auto px-6 py-10">
        <Link
          href="/app/coach/prospects"
          className="inline-flex items-center gap-1 text-sm text-zinc-600 hover:text-kf-red mb-4"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Prospects
        </Link>
        <h1 className="font-display text-3xl mb-2">Invite a prospect</h1>
        <p className="text-zinc-600 mb-8">
          Generate a personal assessment link, then send it from your own email
          so it lands in their inbox as a real note from you — not a system
          message.
        </p>

        {generated ? (
          <GeneratedInviteCard invite={generated} coachName={coachDisplayName} />
        ) : (
        <Card>
          <CardHeader>
            <CardTitle>Generate invite link</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={submit} className="space-y-5">
              <div>
                <Label htmlFor="name">Prospect's full name</Label>
                <Input
                  id="name"
                  type="text"
                  value={form.data.name}
                  onChange={(e) => form.setData("name", e.target.value)}
                  placeholder="Jane Doe"
                  required
                />
                {form.errors.name && (
                  <p className="text-sm text-red-600 mt-1">{form.errors.name}</p>
                )}
              </div>

              <div>
                <Label htmlFor="email">Prospect's email</Label>
                <Input
                  id="email"
                  type="email"
                  value={form.data.email}
                  onChange={(e) => form.setData("email", e.target.value)}
                  placeholder="jane@example.com"
                  required
                />
                {form.errors.email && (
                  <p className="text-sm text-red-600 mt-1">{form.errors.email}</p>
                )}
              </div>

              <p className="rounded-md bg-zinc-50 border border-zinc-200 px-3 py-2 text-xs text-zinc-600">
                They'll receive the peer-group assessment — the Integrated Life
                Wheel plus 10 questions (about 10–15 minutes), ending in a
                personalized report on how a peer group could help them.
              </p>

              <Button
                type="submit"
                variant="gradient"
                size="lg"
                disabled={form.processing}
              >
                {form.processing ? "Generating…" : "Generate invite link"}
              </Button>
            </form>
          </CardContent>
        </Card>
        )}
      </div>
    </CoachLayout>
  )
}
